import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { useEffect, useRef, useState, CSSProperties } from "react";
import { Toast, ToastMessage } from "primereact/toast";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { OtherUserModel } from "../../models/all-user/edit-user-model";
import ClipLoader from "react-spinners/ClipLoader";
import routeCodeServices from "../../services/route-codes/routecode.service";
import subcategoryServices from "../../services/subCategory/subCategory.services";
// import categoryServices from "../../services/category/category.services"; // Import your category service
import CategoryService from "../../services/category/category.service";

interface DataTableProps {
  data: OtherUserModel[];
  handleAddSuccess: () => void;
}

interface ProductDetailModel {
  routeCodeID: number;
  name: string;
  description: string;
}

const SubCategoryDataTable: React.FC<DataTableProps> = (props) => {
  const menuLeft: any = useRef(null);
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [allRoutes, setAllRoutes] = useState<any>();
  const [categories, setCategories] = useState<any[]>([]); // State to store categories
  const [showEditModal, setShowEditModal] = useState(false);
  const [loader, setLoader] = useState<boolean>();
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedRouteCode, setSelectedRouteCode] =
    useState<ProductDetailModel | null>(null);

  const override: CSSProperties = {
    display: "block",
    margin: "0 auto",
    borderColor: "red",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity: severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  useEffect(() => {
    setAllRoutes(props.data);
    fetchCategories(); // Fetch categories on component mount
  }, [props.data]);

  const fetchCategories = async () => {
    try {
      const response = await CategoryService.getCategory();
      const categories = response.map((category: any) => ({
        id: category.categoryID, // Assuming categoryID is the unique identifier
        name: category.name, // Assuming name is the category name
      }));

      setCategories(categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const nameEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        type="text"
        value={options.value}
        style={{ width: "95%", border: "1px solid #090" }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          options.editorCallback!(e.target.value)
        }
      />
    );
  };

  const categoryEditor = (options: ColumnEditorOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={categories}
        optionLabel="name" // Assuming 'name' is the display field
        optionValue="id" // Assuming 'id' is the value field
        onChange={(e) => options.editorCallback!(e.value)}
        placeholder="Select a Category"
        style={{ width: "100%" }}
      />
    );
  };

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    let _data = [...allRoutes];
    let { newData, index } = event;
    if (_data[index] !== newData) {
      let putData = {
        subCategoryID: newData.subCategoryID,
        categoryID: newData.categoryID,
        name: newData.name,
        description: newData.description,
      };

      try {
        await subcategoryServices.updateSubCategory(putData);
        props.handleAddSuccess();
        showMessage("Success", "Sub-category updated successfully", "success");
      } catch (error) {
        showMessage("Error", "Failed to update sub-category", "error");
        console.error("Error updating sub-category:", error);
      }
    }
  };

  const deleteRouteCode = async () => {
    if (!selectedRouteCode) return;

    try {
      await routeCodeServices.deleteRouteCode(selectedRouteCode.routeCodeID);
      props.handleAddSuccess();
      showMessage("Success", "Sub-category deleted successfully", "success");
    } catch (error) {
      showMessage("Error", "Failed to delete sub-category", "error");
      console.error("Error deleting sub-category:", error);
    } finally {
      setSelectedRouteCode(null);
      setShowDeleteDialog(false);
    }
  };

  const onHideDeleteDialog = () => {
    setSelectedRouteCode(null);
    setShowDeleteDialog(false);
  };

  const deleteTemplate = (rowData: ProductDetailModel) => {
    return (
      <Button
        icon="pi pi-trash"
        onClick={() => confirmDelete(rowData)}
        style={{
          border: "none",
          color: "gray",
          background: "none",
          borderRight: "120px",
        }}
        className="p-button-rounded"
      />
    );
  };

  const confirmDelete = (rowData: ProductDetailModel) => {
    setSelectedRouteCode(rowData);
    setShowDeleteDialog(true);
  };

  return (
    <>
      {loader ? (
        <div className="flex-align-items-center justify-content-center p-4">
          <ClipLoader
            loading={loader}
            size={50}
            aria-label="Loading Spinner"
            data-testid="loader"
            cssOverride={override}
            color="#90ee90"
          />
        </div>
      ) : (
        <>
          <Toast ref={toast} position="top-center" />
          <div className="col-12">
            <div className="card border-noround p-2">
              <DataTable
                value={allRoutes?.length > 0 ? allRoutes : allRoutes}
                editMode="row"
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                stripedRows
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                onRowEditComplete={onRowEditComplete}
              >
                <Column
                  field="name"
                  header="Name"
                  editor={nameEditor}
                  style={{ width: "32%" }}
                />
                <Column
                  field="categoryID"
                  header="Category"
                  editor={categoryEditor}
                  style={{ width: "12%" }}
                />
                <Column
                  field="description"
                  header="Description"
                  editor={nameEditor}
                  style={{ width: "32%" }}
                />
                <Column
                  field="isDeleted"
                  header="isDeleted"
                  editor={nameEditor}
                  style={{ width: "32%" }}
                />
                <Column
                  rowEditor
                  header="Action"
                  headerStyle={{ textAlign: "right" }}
                />
              </DataTable>
              <Menu
                popupAlignment={"left"}
                popup
                ref={menuLeft}
                id="popup_menu_left"
              />
            </div>
          </div>
          <Dialog
            visible={showEditModal}
            className="quickview w-50"
            breakpoints={{ "960px": "75vw", "641px": "90vw" }}
            modal
            onHide={() => setShowEditModal(false)}
          />
        </>
      )}
    </>
  );
};

export default SubCategoryDataTable;
